import {
  Box,
  Button,
  Grid,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { number, object, string } from "yup";
import SelectOffice from "components/Textfields/SelectOffice";
import SelectRole from "components/Textfields/SelectRole";
import SelectDesignation from "components/Textfields/SelectDesignation";
import accountService from "services/account-service";

const style = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  position: "absolute",
  backgroundColor: "#e9e9e9",
  boxShadow: "3px 2px 20px 3px rgba(0, 0, 0, 0.3)",
  borderRadius: "10px",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  p: 2,
  overflow: "auto",
  "@media (min-width: 10px)": {
    height: "90vh",
    width: "85vw",
  },

  "@media (min-width: 480px)": {
    height: "85vh",
    width: "80vw",
  },

  "@media (min-width: 640px)": {
    height: "75vh",
    width: "70vw",
  },

  "@media (min-width: 768px)": {
    height: "75vh",
    width: "70vw",
  },

  "@media (min-width: 1024px)": {
    height: "80vh",
    width: "70vw",
  },

  "@media (min-width: 1082px)": {
    height: "80vh",
    width: "65vw",
  },
};

export default function AddMELCModal({
  open,
  handleClose,
  updateTableFunction,
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const formik = useFormik({
    initialValues: {
      username: "",
      password: "",
      role: "",
      designationId: null,
      officeId: null,
    },

    validationSchema: object().shape({
      username: string().required("Required"),
      password: string().required("Required"),
      role: string().required("Required"),
      designationId: number().nullable(),
      officeId: number().nullable(),
    }),
    onSubmit: () => {
      setLoading(true);
      setError("");

      const { role, ...otherValues } = formik.values;
      const lowerCasedRole = role.toLowerCase();

      accountService
        .register({ role: lowerCasedRole, ...otherValues })
        .then(() => {
          updateTableFunction();
        })
        .catch((err) => {
          setError(err?.message);
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });

  useEffect(() => {
    if (formik?.values?.role === "admin") {
      const { officeId, ...otherValues } = formik.values;

      const areAllValuesFilled = Object.values(otherValues).every(
        (value) => !!value
      );

      setDisabled(areAllValuesFilled);

      formik.values.officeId = null;
    } else {
      const areAllValuesFilled = Object.values(formik.values).every(
        (value) => !!value
      );

      setDisabled(areAllValuesFilled);
    }
  }, [formik.values]);

  return (
    <Modal
      open={open}
      onClose={() => {
        handleClose();
      }}
    >
      <Box
        variant="form"
        component="form"
        onSubmit={formik.handleSubmit}
        autoComplete="off"
        sx={style}
      >
        <Box
          sx={{
            position: "absolute",
            right: 20,
            cursor: "pointer",
            zIndex: 100,
          }}
        >
          <IconButton onClick={handleClose} sx={{ p: 0 }}>
            <CancelIcon />
          </IconButton>
        </Box>
        <Box>
          <Box
            sx={{
              backgroundColor: "blue",
              borderRadius: "10px",
              width: "70%",
              p: 2,
              mb: 2,
              ml: 4,
            }}
          >
            <Typography
              sx={{
                ml: 3,
                fontSize: 25,
                fontWeight: "500",
                color: "#fff",
                fontFamily: "Poppins",
              }}
            >
              ADD USER
            </Typography>
          </Box>
          {error}
          <Box
            sx={{
              backgroundColor: "rgba(255, 255, 255, 1)",
              boxShadow: "8px 8px 15px 3px rgba(0, 0, 0, 0.3)",
              borderRadius: "10px",
              p: 4,
              mx: 4,
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  name="username"
                  label={`Username ${formik?.values?.username ? "" : "*"}`}
                  size="small"
                  disabled={loading}
                  value={formik.values.username}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBLur}
                  error={
                    formik.touched.username && Boolean(formik.errors.username)
                  }
                  helperText={formik.touched.username && formik.errors.username}
                  variant="outlined"
                  sx={{ pr: 5 }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  id="password"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  size="small"
                  disabled={loading}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBLur}
                  error={
                    formik.touched.password && Boolean(formik.errors.password)
                  }
                  helperText={formik.touched.password && formik.errors.password}
                  fullWidth
                  sx={{ pr: 5 }}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        onKeyPress={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <VisibilityIcon size={18} sx={{ color: "#606468" }} />
                        ) : (
                          <VisibilityOffIcon
                            size={18}
                            sx={{ color: "#606468" }}
                          />
                        )}
                      </IconButton>
                    ),
                    sx: {
                      borderRadius: "7px",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <SelectRole
                  label={`Role ${formik.values.role ? "" : "*"}`}
                  name="role"
                  width="100%"
                  value={formik.values.role}
                  onChange={(fieldName, selectedValue) => {
                    formik.setFieldValue("role", selectedValue);
                  }}
                  onBlur={formik.handleBLur}
                  error={formik.touched.role && Boolean(formik.errors.role)}
                  helperText={formik.touched.role && formik.errors.role}
                  sx={{
                    width: "100%",
                    pr: 5,
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "black !important",
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <SelectDesignation
                  label={`Location ${formik.values.designationId ? "" : "*"}`}
                  name="designationId"
                  width="100%"
                  disabled={formik.values.role === "admin"}
                  value={formik.values.designationId}
                  onChange={(fieldName, selectedValue) => {
                    formik.setFieldValue("designationId", selectedValue);
                  }}
                  onBlur={formik.handleBLur}
                  error={
                    formik.touched.designationId &&
                    Boolean(formik.errors.designationId)
                  }
                  helperText={
                    formik.touched.designationId && formik.errors.designationId
                  }
                  sx={{
                    width: "100%",
                    pr: 5,
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "black !important",
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <SelectOffice
                  label={`Office ${formik.values.officeId ? "" : "*"}`}
                  name="officeId"
                  width="100%"
                  disabled={formik.values.role === "admin"}
                  value={formik.values.officeId}
                  onChange={(fieldName, selectedValue) => {
                    formik.setFieldValue("officeId", selectedValue);
                  }}
                  onBlur={formik.handleBLur}
                  error={
                    formik.touched.officeId && Boolean(formik.errors.officeId)
                  }
                  helperText={formik.touched.officeId && formik.errors.officeId}
                  sx={{
                    width: "100%",
                    pr: 5,
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "black !important",
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
            p: 2,
            zIndex: 10,
          }}
        >
          <Button
            disabled={!disabled}
            type="submit"
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#564ee2",
              color: "white",
              py: 1,
              width: "10vw",
              minWidth: "100px",
              "&:hover": {
                color: "black",
                backgroundColor: "#11edd2",
              },
            }}
          >
            Add User
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
